import { NoSsr } from "@mui/base";
import type { FunctionComponent } from "react";
import { Suspense, useEffect } from "react";
import { graphql, useQueryLoader } from "react-relay";

import type { ContentSchemaRenderProps } from "~/components/content/content-schema-render";
import { ImageSizeStyle, useContentUtils } from "~/lib/hooks";
import { isValidUrl } from "~/lib/utils";
import type { digitalArchiveLinkCheckPathQuery } from "~/queries/__generated__/digitalArchiveLinkCheckPathQuery.graphql";

import { DigitalArchiveLinkImage } from "./digital-archive-link-image";
import { DigitalArchiveLinkUpdatedDate } from "./digital-archive-link-updated-date";
import {
  ImageContainer,
  LeftContainer,
  RightContainer,
  RootContainer,
  TitleContainer,
  TitleText,
  UpdatedDateContainer,
} from "./styles";

export const query = graphql`
  query digitalArchiveLinkCheckPathQuery($url: String!, $imageSize: String!) {
    checkPath(url: $url) {
      ...digitalArchiveLinkImageUrlPath
      ...digitalArchiveLinkUpdatedDateUrlPath
    }
  }
`;

export type Props = {
  href?: string;
  lazyload?: boolean;
} & ContentSchemaRenderProps;

export const DigitalArchiveLink: FunctionComponent<Props> = ({
  href = "",
  lazyload,
  schemaNode,
}) => {
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<digitalArchiveLinkCheckPathQuery>(query);

  const { extractTextFromSchemaNodes } = useContentUtils();

  useEffect(() => {
    if (!isValidUrl(href)) return;

    const url = new URL(href);
    loadQuery({ imageSize: ImageSizeStyle["237x147"], url: url.pathname });

    return disposeQuery;
  }, [loadQuery, disposeQuery, href]);

  return (
    <RootContainer>
      <LeftContainer>
        <TitleContainer>
          <TitleText>{extractTextFromSchemaNodes(schemaNode)}</TitleText>
        </TitleContainer>
        <UpdatedDateContainer>
          <NoSsr>
            <Suspense>
              {queryReference && <DigitalArchiveLinkUpdatedDate queryReference={queryReference} />}
            </Suspense>
          </NoSsr>
        </UpdatedDateContainer>
      </LeftContainer>

      <RightContainer>
        <ImageContainer>
          <NoSsr>
            <Suspense>
              {queryReference && (
                <DigitalArchiveLinkImage lazyload={lazyload} queryReference={queryReference} />
              )}
            </Suspense>
          </NoSsr>
        </ImageContainer>
      </RightContainer>
    </RootContainer>
  );
};

DigitalArchiveLink.displayName = "DigitalArchiveLink";
